import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EnvironmentInjector,
  inject,
  runInInjectionContext,
  signal
} from '@angular/core';
import {AsyncPipe, JsonPipe, NgClass} from "@angular/common";
import {Image} from "primeng/image";
import {ButtonModule} from "primeng/button";
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  ParamMap,
  Router,
  RouterLink
} from "@angular/router";
import {UserService} from "../../services/user.service";
import {filter, map, of} from "rxjs";
import {AuthService} from "../../services/auth/auth.service";
import {RouteDataService} from "../../services/route-data.service";
import {initialSignupRoute} from '../../pages/signup/specific-signup.routes';
import {BackButtonComponent} from "./back-button/back-button.component";
import {HeaderButtonsComponent} from "./header-buttons/header-buttons.component";
import {PaymentService} from "../../services/payment/payment.service";
import {ABService} from "../../services/a-b.service";
import {getDeepestChildRoute, isNative} from "../../utils";
import {SignupButtonComponent} from "../signup-button/signup-button.component";
import {is} from "typia";
import {TranslocoService} from "@jsverse/transloco";
import {LanguageSwitcherComponent} from "../language-switcher/language-switcher.component";
import {PlatformService} from "../../services/platform.service";
import {AnalyticsService} from "../../services/analytics.service";

@Component({
  selector: 'homey-header',
  standalone: true,
  imports: [
    Image,
    ButtonModule,
    NgClass,
    BackButtonComponent,
    HeaderButtonsComponent,
    AsyncPipe,
    SignupButtonComponent,
    RouterLink,
    LanguageSwitcherComponent,
  ],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent {
  protected headerType: 'grey' | 'default' = 'default';

  protected readonly abService = inject(ABService);
  protected readonly activatedRoute = inject(ActivatedRoute);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly locoService = inject(TranslocoService)
  private readonly platformService = inject(PlatformService);
  private readonly analyticsService = inject(AnalyticsService);

  protected readonly user$ = this.userService.user$;
  protected readonly userSignal = this.userService.user;
  protected readonly lastUserStatus = this.userService.lastUserStatus;
  protected readonly showHeader = computed(
  () => this.lastUserStatus() === 'unauthorized' && false
  );
  private readonly paymentService = inject(PaymentService);
  protected readonly percentage = this.paymentService.percentageOff;

  protected loading = false;
  private readonly params = signal<ParamMap | undefined>(undefined);
  protected readonly signupToApplyRoute = computed(
    () => this.routeDataService.routeData().signupToApplyRoute ?? initialSignupRoute
  )

  protected readonly loaded = signal(false);

  protected readonly signupToApplyRouteIncludingInfo = computed(
    () => {
      const params = this.params();

      if (!params) {
        return of({});
      }

      return runInInjectionContext(this.environmentInjector, () => this.routeDataService.routeData().signupToApplyRouteParams?.(params) ?? of({}));
    }
  )

  protected readonly signupToApplyText = computed(
    () => {
      const text = this.routeDataService.routeData().signupToApplyText;

      return text ? this.locoService.translate(text) : 'Try for free'
    }
  )

  protected readonly headerStyle = computed(
    () => {
      if(!this.loaded()) {
        return 'loading';
      } else {
        return this.routeDataService.routeData().headerStyle ?? 'sub-page'
      }
    }
  )

  protected readonly promotionHeader = this.abService.promotionHeader;

  protected readonly showPromotionBanner = computed(() => {
    if (!this.platformService.isBrowser) {
      return false;
    }

    if (isNative) {
      return false;
    }

    const routeData = this.routeDataService.routeData();
    const user = this.userSignal();
    const promotion = this.promotionHeader();

    if (!routeData.showPromotionHeader) {
      return false;
    }

    if (!promotion || !promotion.enabled) {
      return false;
    }

    if (!user) {
      return false;
    }

    return user.status === 'unauthorized' || user.status === 'signup-flow';
  });


  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly routeDataService: RouteDataService
    ) {

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          this.loaded.set(true);
          let route = this.activatedRoute;
          // let data = {};
          while (route.firstChild) {
            route = route.firstChild;
            // data = {...data, ...route.snapshot.data};
          }

          this.params.set(getDeepestChildRoute(route).snapshot.paramMap);

          if (route.snapshot.data['headerType']) {
            this.headerType = route.snapshot.data['headerType'];
          } else {
            this.headerType = 'default';
          }
        })
      )
      .subscribe();

    this.router.events.subscribe((e) => {
      this.navigationInterceptor(e);
    })
  }

  // Shows and hides the loading spinner during RouterEvent changes
  navigationInterceptor(event: Event): void {
    if (event instanceof NavigationStart) {
      this.loading = true
    }
    if (event instanceof NavigationEnd) {
      this.loading = false
    }

    // Set loading state to false in both of the below events to hide the spinner in case a request fails
    if (event instanceof NavigationCancel) {
      this.loading = false
    }
    if (event instanceof NavigationError) {
      this.loading = false
    }
  }

  public onUpdatePaymentDetails() {
    this.paymentService.openFixPaymentPage();
  }

  public onResubscribeClick() {
    this.paymentService.openResubscribeDialog();
  }

  protected onPromotionHeaderClick() {
    const promotion = this.promotionHeader();

    if (!promotion) {
      return;
    }

    void this.analyticsService.sendEvent('promotion-header-clicked', {
      link: promotion.link,
    });
  }

  protected readonly initialSignupRoute = initialSignupRoute;
  protected readonly is = is;
  protected readonly isNative = isNative;
}
